"use client";

/**
 * The first call (spec 2026-10-14 §6.2; mockups/bash-phone-2026-10-14.html,
 * chapter 1). Bash asks for their number (a linked chat's is one tap away),
 * shows the consent line and hands over his card, rings from his own number,
 * and they key in the code on the screen. A few seconds after "Call me" the
 * card folds to one line and the code grows: a timer, no server state. The
 * status is polled every 2 s while he calls; on a match the PIN step follows.
 * The heading moves with the moment, as the mock has it.
 *
 * The beats render as siblings with no wrapper, so each host lays them out in
 * its own column: the first-open dialog and the page.
 */
import { useEffect, useId, useRef, useState } from "react";
import { PHONE_PIN_MAX_DIGITS, PHONE_PIN_MIN_DIGITS } from "@bash/shared";
import { buttonVariants } from "@/components/ui/button";
import { getPhoneStatus, setPin, startVerification } from "@/lib/api/phone";
import { cn } from "@/lib/utils";
import { BashFace } from "./BashFace";
import { PHONE_COPY, displayNumber } from "./copy";
import { CheckMarkGlyph, GLYPH_STROKE } from "./glyphs";
import { downloadBashCard } from "./vcard";

/** The classes a host dresses the flow in. */
export interface FirstCallSkin {
  face: string;
  title: string;
  lede: string;
  field: string;
  input: string;
  actions: string;
  primary: string;
  secondary: string;
}

/** The page's and the first-open dialog's: the settings rows' input and the house buttons. */
export const PAGE_SKIN: FirstCallSkin = {
  face: "size-14",
  title: "text-4.5 font-semibold text-fg-default",
  lede: "text-3.5 text-fg-subtle",
  field: "flex w-full max-w-85 flex-col gap-1.5",
  input:
    "rounded-8 border border-border-default bg-bg-base px-2.5 py-1.5 text-3.5 text-fg-default transition-colors focus:border-gold-molten-edge focus:outline-none",
  actions: "flex flex-wrap gap-2.5",
  primary: buttonVariants({ size: "sm" }),
  secondary: buttonVariants({ variant: "secondary", size: "sm" }),
};

type Step =
  | { kind: "ask"; note?: string; retry?: boolean }
  | { kind: "calling"; code: string; bashNumber: string; folded: boolean }
  | { kind: "verified" }
  /** The number was theirs already (409 already_verified): the same end, with nothing left to ask. */
  | { kind: "known" };

const POLL_MS = 2000;
/** "A few seconds" after Call me, the card folds to one line and the code grows. */
export const FOLD_MS = 4000;
const PIN_SHAPE = new RegExp(
  `^\\d{${PHONE_PIN_MIN_DIGITS},${PHONE_PIN_MAX_DIGITS}}$`
);

export function FirstCallFlow({
  suggested,
  onDone,
  onSkip,
  title = PHONE_COPY.askTitle,
  lede = PHONE_COPY.ask,
  withPin = true,
  skin = PAGE_SKIN,
}: {
  /** A linked chat's number, E.164, offered for one tap. */
  suggested?: string;
  onDone: () => void;
  /** "Not now" on the ask. Absent, the ask has no way out (the page with no number yet). */
  onSkip?: () => void;
  /** The ask's heading and its line; the onboarding beat's by default. */
  title?: string;
  lede?: string | null;
  /** The PIN step after a match: the first number asks for one, another number added later does not. */
  withPin?: boolean;
  skin?: FirstCallSkin;
}) {
  const id = useId();
  const [number, setNumber] = useState("");
  const [step, setStep] = useState<Step>({ kind: "ask" });
  const [busy, setBusy] = useState(false);
  const [pin, setPinValue] = useState("");
  const [pinNote, setPinNote] = useState<string | null>(null);
  const alive = useRef(true);
  const poll = useRef<ReturnType<typeof setInterval> | null>(null);
  const fold = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stop = () => {
    if (poll.current) clearInterval(poll.current);
    if (fold.current) clearTimeout(fold.current);
    poll.current = null;
    fold.current = null;
  };

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (poll.current) clearInterval(poll.current);
      if (fold.current) clearTimeout(fold.current);
    };
  }, []);

  const call = async () => {
    setBusy(true);
    const r = await startVerification(number.trim());
    if (!alive.current) return;
    setBusy(false);
    if (!r.ok) {
      if (r.error === "already_verified") {
        setStep({ kind: "known" });
        return;
      }
      setStep({
        kind: "ask",
        note: PHONE_COPY.errors[r.error] ?? PHONE_COPY.errors.unknown,
      });
      return;
    }
    const { code, bashNumber } = r.body;
    setStep({ kind: "calling", code, bashNumber, folded: false });
    fold.current = setTimeout(() => {
      setStep((s) => (s.kind === "calling" ? { ...s, folded: true } : s));
    }, FOLD_MS);
    poll.current = setInterval(() => {
      void getPhoneStatus()
        .then((s) => {
          if (!s || s.state === "calling" || !alive.current) return;
          stop();
          setStep(
            s.state === "verified"
              ? { kind: "verified" }
              : { kind: "ask", note: PHONE_COPY.failed, retry: true }
          );
        })
        .catch(() => undefined);
    }, POLL_MS);
  };

  const savePin = async () => {
    if (!PIN_SHAPE.test(pin)) {
      setPinNote(PHONE_COPY.pinBad);
      return;
    }
    setBusy(true);
    try {
      if ((await setPin(pin)) === "ok") {
        onDone();
        return;
      }
      setPinNote(PHONE_COPY.pinBad);
    } catch {
      setPinNote(PHONE_COPY.errors.unknown);
    }
    setBusy(false);
  };

  // His face with the check: a number that is theirs, matched now or already.
  const verifiedFace = (
    <span className="relative inline-flex">
      <BashFace className={skin.face} alt={PHONE_COPY.bash} />
      <span className="absolute right-0 bottom-0 flex size-5.5 items-center justify-center rounded-full bg-status-success text-fg-inverse ring-2 ring-bg-base">
        <CheckMarkGlyph className={cn("size-3", GLYPH_STROKE)} />
      </span>
    </span>
  );

  if (step.kind === "known") {
    return (
      <>
        {verifiedFace}
        <h2 className={skin.title}>{PHONE_COPY.alreadyYours}</h2>
        <div className={skin.actions}>
          <button type="button" className={skin.primary} onClick={onDone}>
            {PHONE_COPY.continue}
          </button>
        </div>
      </>
    );
  }

  if (step.kind === "verified") {
    return (
      <>
        {verifiedFace}
        <h2 className={skin.title}>{PHONE_COPY.verified}</h2>
        {withPin ? (
          <>
            <p className={skin.lede}>{PHONE_COPY.pinAsk}</p>
            <div className={skin.field}>
              <input
                aria-label={PHONE_COPY.pinField}
                className={cn(
                  skin.input,
                  "tracking-[0.3em] placeholder:tracking-normal"
                )}
                type="password"
                inputMode="numeric"
                autoComplete="off"
                placeholder={PHONE_COPY.pinPlaceholder}
                value={pin}
                onChange={(e) =>
                  setPinValue(
                    e.target.value
                      .replace(/\D/g, "")
                      .slice(0, PHONE_PIN_MAX_DIGITS)
                  )
                }
              />
            </div>
            {pinNote ? (
              <p className="text-3 text-fg-subtle" role="status">
                {pinNote}
              </p>
            ) : null}
            <div className={skin.actions}>
              <button
                type="button"
                className={skin.primary}
                disabled={busy || !pin}
                onClick={() => void savePin()}
              >
                {PHONE_COPY.setPin}
              </button>
              <button type="button" className={skin.secondary} onClick={onDone}>
                {PHONE_COPY.notNow}
              </button>
            </div>
          </>
        ) : (
          <div className={skin.actions}>
            <button type="button" className={skin.primary} onClick={onDone}>
              {PHONE_COPY.continue}
            </button>
          </div>
        )}
      </>
    );
  }

  if (step.kind === "calling") {
    const shown = displayNumber(step.bashNumber);
    return (
      <>
        {step.folded ? (
          <div className="flex items-center gap-3 rounded-full border border-border-subtle bg-bg-elevated py-1.5 pr-4 pl-1.5">
            <BashFace className="size-10" />
            <div className="text-left">
              <div className="text-3.5 font-semibold text-fg-default">
                {PHONE_COPY.bash}
              </div>
              <div className="text-3 text-fg-subtle tabular-nums">{shown}</div>
            </div>
          </div>
        ) : (
          <div className="flex w-full max-w-70 flex-col items-center rounded-16 border border-border-subtle bg-bg-elevated px-5 py-5 text-center">
            <BashFace className="size-22" />
            <div className="mt-3 text-4 font-semibold text-fg-default">
              {PHONE_COPY.bash}
            </div>
            <div className="text-3.5 text-fg-muted tabular-nums">{shown}</div>
            <button
              type="button"
              className={cn(skin.primary, "mt-5 w-full")}
              onClick={() => void downloadBashCard(step.bashNumber)}
            >
              {PHONE_COPY.save}
            </button>
            <p className="mt-2.5 text-3 text-fg-subtle">{PHONE_COPY.saveWhy}</p>
          </div>
        )}
        {step.folded ? (
          <p className="mt-6 inline-flex items-center gap-2 text-3.5 font-semibold text-gold-ink">
            <span
              aria-hidden="true"
              className="size-2 animate-pulse rounded-full bg-gold-molten-mid motion-reduce:animate-none"
            />
            {PHONE_COPY.ringing}
          </p>
        ) : (
          <p className="mt-5 text-3.5 text-fg-default">
            {PHONE_COPY.calling(shown)}
          </p>
        )}
        <p className="mt-1.5 text-3 text-fg-subtle">{PHONE_COPY.keyIn}</p>
        {step.folded ? (
          <>
            <p className="sr-only">{step.code}</p>
            <div className="mt-3 flex gap-2.5" aria-hidden="true">
              {step.code.split("").map((d, i) => (
                <span
                  key={i}
                  className="flex h-15 w-12 items-center justify-center rounded-12 border border-border-default bg-bg-elevated text-8 font-semibold text-fg-default tabular-nums"
                >
                  {d}
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className="text-7 font-semibold tracking-[0.3em] text-fg-default tabular-nums">
            {step.code}
          </p>
        )}
      </>
    );
  }

  return (
    <>
      <BashFace className={skin.face} alt={PHONE_COPY.bash} />
      <h2 className={skin.title}>{title}</h2>
      {lede ? <p className={skin.lede}>{lede}</p> : null}
      <div className={skin.field}>
        <label
          htmlFor={`${id}-number`}
          className="text-3 font-semibold text-fg-subtle"
        >
          {PHONE_COPY.numberLabel}
        </label>
        <input
          id={`${id}-number`}
          className={skin.input}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder={PHONE_COPY.numberPlaceholder}
          value={number}
          onChange={(e) => setNumber(e.target.value)}
        />
        {suggested && number.trim() !== suggested ? (
          <button
            type="button"
            className="rounded-full border border-border-default bg-bg-elevated px-3 py-1.5 text-3.5 text-fg-default tabular-nums transition-colors hover:border-gold-molten-edge"
            onClick={() => setNumber(suggested)}
          >
            {displayNumber(suggested)}
          </button>
        ) : null}
        {suggested ? (
          <p className="text-3 text-fg-subtle">{PHONE_COPY.suggestedHint}</p>
        ) : null}
      </div>
      {step.note ? (
        <p className="mt-2 text-3.5 text-status-error" role="alert">
          {step.note}
        </p>
      ) : null}
      <p className="mt-3 max-w-80 text-3 text-fg-subtle">
        {PHONE_COPY.consent}
      </p>
      <div className={skin.actions}>
        <button
          type="button"
          className={skin.primary}
          disabled={busy || !number.trim()}
          onClick={() => void call()}
        >
          {step.retry ? PHONE_COPY.tryAgain : PHONE_COPY.callMe}
        </button>
        {onSkip ? (
          <button type="button" className={skin.secondary} onClick={onSkip}>
            {PHONE_COPY.notNow}
          </button>
        ) : null}
      </div>
    </>
  );
}
